import { storageKey, writeLocalJson, readLocalJson } from '@authe/fe-commons/storage';
import { stableHash } from '@authe/fe-commons/keys';
import { createLogger, isDebugEnabled } from '@authe/fe-commons/logging';
import { computeBackoff as computeBackoff$1 } from '@authe/fe-commons/async';
import { convertRegionToBackupRegion, isRegionEnabled, convertRegionToAlias } from '@authe/fe-commons/region';
import * as React5 from 'react';
import { jsx } from 'react/jsx-runtime';

// src/config.ts
var STREAMING_PACKAGE = "streaming";
var STREAM_BROADCAST_CHANNEL = "authe.stream";
var STREAM_ELECTION_CHANNEL = `${STREAM_BROADCAST_CHANNEL}.election`;
var STREAM_EVENTS_CHANNEL = `${STREAM_BROADCAST_CHANNEL}.events`;
var STREAM_REGION_KEY = storageKey(STREAMING_PACKAGE, "region");
var STREAM_BACKOFF_BASE_MS = 1e3;
var STREAM_BACKOFF_MAX_MS = 3e4;
var STREAM_BACKOFF_FACTOR = 2;
var STREAM_BACKOFF_JITTER = 0.3;
var STREAM_HEARTBEAT_INTERVAL_MS = 15e3;
var STREAM_HEARTBEAT_TIMEOUT_MS = 45e3;
var STREAM_PING_INTERVAL_MS = 2e4;
var STREAM_PING_TIMEOUT_MS = 1e4;
var STREAM_CONNECT_TIMEOUT_MS = 3e4;
var STREAM_HANDSHAKE_TIMEOUT_MS = 1e4;
var STREAM_HTTP_TIMEOUT_MS = 15e3;
var STREAM_REGION_FAILOVER_THRESHOLD = 3;
var STREAM_DEDUPE_CAPACITY = 5e3;
var STREAM_LEADER_HEARTBEAT_MS = 2e3;
var STREAM_LEADER_STALE_MS = 6e3;

// src/errors.ts
var ChannelScopedError = class extends Error {
  /** Discriminant so {@link isChannelScopedError} works across bundle realms. */
  channelScoped = true;
  /** The original AppSync error payload, preserved for {@link describeError}. */
  payload;
  constructor(payload) {
    super(describeError(payload));
    this.name = "ChannelScopedError";
    this.payload = payload;
  }
};
function isChannelScopedError(error) {
  return Boolean(
    error && typeof error === "object" && error.channelScoped === true
  );
}
function describeError(error) {
  if (error == null) return String(error);
  if (typeof error === "string") return error;
  if (typeof error === "number" || typeof error === "boolean") return String(error);
  if (error instanceof Error) return error.message || error.name || "Error";
  if (typeof Event !== "undefined" && error instanceof Event) {
    const e = error;
    const bits = [e.type || "event"];
    if (e.code != null) bits.push(`code=${e.code}`);
    if (e.reason) bits.push(`reason=${e.reason}`);
    if (e.message) bits.push(e.message);
    return bits.join(" ");
  }
  if (typeof error === "object") {
    const obj = error;
    if (Array.isArray(obj.errors) && obj.errors.length > 0) {
      return obj.errors.map((e) => describeError(e)).join("; ");
    }
    if (typeof obj.message === "string") {
      return typeof obj.errorType === "string" ? `${obj.errorType}: ${obj.message}` : obj.message;
    }
    if (typeof obj.errorType === "string") return obj.errorType;
    try {
      return JSON.stringify(error);
    } catch {
      return Object.prototype.toString.call(error);
    }
  }
  return String(error);
}

// src/model/status.ts
var ConnectionStatus = {
  /** Not started. */
  IDLE: "idle",
  /** Opening the transport. */
  CONNECTING: "connecting",
  /** Live + receiving. */
  OPEN: "open",
  /** Dropped; backing off before the next attempt. */
  RECONNECTING: "reconnecting",
  /** Open but the healthcheck is stale; recovering. */
  DEGRADED: "degraded",
  /** Intentionally stopped. */
  CLOSED: "closed",
  /** Unrecoverable error surfaced to the app. */
  ERROR: "error"
};
function initialConnectionState(region) {
  return {
    status: ConnectionStatus.IDLE,
    region,
    isLeader: false,
    retries: 0,
    lastEventAt: null
  };
}

// src/model/channel.ts
var ALL_CHANNELS = "*";
var DROP_SEGMENTS = /* @__PURE__ */ new Set(["", "main", "*"]);
var TEAM_CHANNEL_PREFIX = "team#";
function teamChannelPrincipal(teamId) {
  return `${TEAM_CHANNEL_PREFIX}${String(teamId).trim()}`;
}
function streamChannelId(scope, stream) {
  const parts = [scope.orgId, scope.tenantId, scope.userId].map((part) => part == null ? "" : String(part).trim()).filter((part) => part !== "" && !DROP_SEGMENTS.has(part));
  parts.push(stream);
  return parts.join("-");
}
function streamChannelIdsForCaller(scope, stream, teamIds = [], options = {}) {
  const { includeGlobal = true } = options;
  const ids = [];
  const add = (id) => {
    if (!ids.includes(id)) ids.push(id);
  };
  if (includeGlobal) add(streamChannelId({}, stream));
  add(streamChannelId({ orgId: scope.orgId, tenantId: scope.tenantId }, stream));
  if (scope.userId != null && String(scope.userId).trim() !== "") {
    add(streamChannelId(scope, stream));
  }
  for (const teamId of teamIds) {
    const trimmed = String(teamId ?? "").trim();
    if (trimmed === "") continue;
    add(
      streamChannelId(
        {
          orgId: scope.orgId,
          tenantId: scope.tenantId,
          userId: teamChannelPrincipal(trimmed)
        },
        stream
      )
    );
  }
  return ids;
}
function pick(obj, ...keys) {
  for (const key of keys) {
    const value = obj[key];
    if (value != null) return value;
  }
  return void 0;
}
var fingerprint = stableHash;
var autheDedupeKey = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const record2 = payload;
  const tx = pick(record2, "canonical_tx_id", "canonicalTxId", "tx_id", "txId", "id");
  if (tx == null) return null;
  const log = pick(record2, "log_index", "logIndex");
  const id = log == null ? String(tx) : `${String(tx)}:${String(log)}`;
  let body;
  try {
    body = JSON.stringify(record2) ?? "";
  } catch {
    return id;
  }
  return `${id}#${fingerprint(body)}`;
};

// src/streams/names.ts
var STREAM_STREAMED_TRANSACTIONS = "streamed-transactions";
var STREAM_ADDRESS_WATCH = "address-watch";
var STREAM_MESSAGES = "messages";
var STREAM_TAGS = "tags";
var STREAM_LABELS = "labels";
var STREAM_NOTIFICATIONS = "notifications";
var STREAM_ENTITIES = "entities";
var STREAM_GROUPS = "groups";
var STREAM_TRANSACTION_GROUPS = "transaction-groups";
var STREAM_COLLABORATION = "collaboration";
var STREAM_CHAIN_TIPS = "chain-tips";
var KNOWN_STREAMS = [
  STREAM_STREAMED_TRANSACTIONS,
  STREAM_ADDRESS_WATCH,
  STREAM_MESSAGES,
  STREAM_TAGS,
  STREAM_LABELS,
  STREAM_NOTIFICATIONS,
  STREAM_ENTITIES,
  STREAM_GROUPS,
  STREAM_TRANSACTION_GROUPS,
  STREAM_COLLABORATION,
  STREAM_CHAIN_TIPS
];
var KNOWN_STREAM_SET = new Set(KNOWN_STREAMS);
function isKnownStream(stream) {
  return KNOWN_STREAM_SET.has(stream);
}

// src/streams/payload.ts
function hasBodyPointer(value) {
  return !!value && typeof value.s3_key === "string" && value.s3_key !== "";
}
var STREAM_EVENT_VERBS = /* @__PURE__ */ new Set([
  "created",
  "updated",
  "deleted"
]);
function readRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function readResourceEvent(payload) {
  const record2 = readRecord(payload);
  if (!record2) return null;
  const stream = typeof record2.stream === "string" ? record2.stream : "";
  if (!stream) return null;
  const verb = typeof record2.event === "string" ? record2.event : "";
  if (!STREAM_EVENT_VERBS.has(verb)) return null;
  const header = readRecord(record2.header);
  const id = (typeof record2.id === "string" && record2.id !== "" ? record2.id : void 0) ?? (typeof header?.id === "string" && header.id !== "" ? header.id : void 0);
  if (!id) return null;
  return {
    stream,
    resourceType: typeof record2.resource_type === "string" ? record2.resource_type : void 0,
    event: verb,
    id,
    header,
    orgId: record2.org_id ?? null,
    tenantId: record2.tenant_id ?? null,
    userId: record2.user_id ?? null
  };
}
function isStreamBatch(value) {
  if (!value || typeof value !== "object") return false;
  const record2 = value;
  return record2.batch === true && Array.isArray(record2.items);
}
function unwrapStreamBatch(payload) {
  return isStreamBatch(payload) ? payload.items : [payload];
}

// src/streams/queries.ts
var SUBSCRIBE_QUERY = (
  /* GraphQL */
  `
  subscription Subscribe($name: String!) {
    subscribe(name: $name) {
      name
      data
    }
  }
`
);
var PING_QUERY = (
  /* GraphQL */
  `
  query Ping {
    ping {
      timestamp
      status
    }
  }
`
);
var PUBLISH_MUTATION = (
  /* GraphQL */
  `
  mutation PublishToChannel($name: String!, $data: AWSJSON!) {
    publishToChannel(name: $name, data: $data) {
      name
      data
    }
  }
`
);
function subscribeDescriptor(channelId) {
  return { id: channelId, query: SUBSCRIBE_QUERY, variables: { name: channelId } };
}
function record(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function selectAutheStreamPayload(raw) {
  const root = record(raw);
  const subscribe = record(record(root?.data)?.subscribe);
  const data = subscribe?.data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data ?? raw;
}
function parsePingResult(raw) {
  const root = record(raw);
  const ping = record(record(root?.data)?.ping) ?? record(root?.ping);
  if (!ping) return null;
  return {
    timestamp: String(ping.timestamp ?? ""),
    status: String(ping.status ?? "")
  };
}
function createAppSyncPing(client) {
  return async (token) => {
    const result = await client.graphql({
      query: PING_QUERY,
      authToken: token
    });
    const parsed = parsePingResult(result);
    if (!parsed || parsed.status.toLowerCase() !== "ok") {
      throw new Error(`ping: unexpected response (${parsed?.status ?? "none"})`);
    }
  };
}
function createAppSyncPublish(client) {
  return async (name, data, token) => {
    await client.graphql({
      query: PUBLISH_MUTATION,
      variables: { name, data: JSON.stringify(data ?? null) },
      authToken: token
    });
  };
}
function defaultSelectPayload(raw) {
  if (raw && typeof raw === "object" && "data" in raw) {
    const data = raw.data;
    if (data && typeof data === "object") {
      const values = Object.values(data);
      return values.length === 1 ? values[0] : data;
    }
    return data;
  }
  return raw;
}
function createAppSyncTransport(options) {
  const select = options.selectPayload ?? defaultSelectPayload;
  const log = options.logger ?? createLogger(STREAMING_PACKAGE).child("appsync");
  return {
    async connect(connectOptions, handlers) {
      const subscriptions = /* @__PURE__ */ new Set();
      const resolved = options.resolve ? options.resolve(connectOptions.url) : {
        client: options.client,
        ping: options.ping,
        ready: options.ready,
        release: options.release
      };
      const client = resolved.client;
      if (!client) {
        throw new Error(
          "AppSync transport requires either `client` or `resolve` to be configured"
        );
      }
      const ping = resolved.ping;
      log.debug("connect", { region: connectOptions.region, url: connectOptions.url });
      if (resolved.ready) await resolved.ready(connectOptions.token);
      handlers.onOpen?.();
      const connection = {
        ping: ping ? () => ping(connectOptions.token) : void 0,
        subscribe(channel) {
          if (!channel.query) {
            handlers.onError(new Error(`AppSync channel "${channel.id}" is missing a query`));
            return () => {
            };
          }
          log.debug("subscribe", { channel: channel.id, variables: channel.variables });
          const observable = client.graphql({
            query: channel.query,
            variables: channel.variables,
            authToken: connectOptions.token
          });
          const sub = observable.subscribe({
            next: (value) => {
              log.debug("message", { channel: channel.id });
              handlers.onMessage(channel.id, select(value, channel));
            },
            error: (error) => {
              log.warn("subscription error", { channel: channel.id, error: describeError(error) });
              if (isChannelScopedError(error)) return;
              handlers.onError(error);
            }
          });
          subscriptions.add(sub);
          return () => {
            sub.unsubscribe();
            subscriptions.delete(sub);
          };
        },
        close() {
          for (const sub of subscriptions) sub.unsubscribe();
          subscriptions.clear();
          resolved.release?.();
          handlers.onClose?.();
        }
      };
      return connection;
    }
  };
}
var WS_OPEN = 1;
var DEFAULT_KEEPALIVE_MS = 3e5;
var DEFAULT_LINGER_MS = 1e3;
function base64Json(value) {
  const json = JSON.stringify(value);
  if (typeof btoa === "function") {
    const bytes = new TextEncoder().encode(json);
    let latin1 = "";
    for (const byte of bytes) latin1 += String.fromCharCode(byte);
    return btoa(latin1);
  }
  return Buffer.from(json, "utf8").toString("base64");
}
function authProtocol(endpoint, token) {
  const b64url = base64Json(authHeader(endpoint, token)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return `header-${b64url}`;
}
function appSyncRealtimeUrl(endpoint) {
  const url = new URL(endpoint);
  if (url.host.includes(".appsync-api.")) {
    const host = url.host.replace(".appsync-api.", ".appsync-realtime-api.");
    return `wss://${host}${url.pathname}`;
  }
  return `wss://${url.host}${url.pathname}/realtime`;
}
function authHeader(endpoint, token) {
  return { host: new URL(endpoint).host, Authorization: token ?? "" };
}
function uuid() {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `sub-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
var RealtimeSocket = class {
  constructor(endpoint, WS, log, lingerMs, handshakeMs) {
    this.endpoint = endpoint;
    this.WS = WS;
    this.log = log;
    this.lingerMs = lingerMs;
    this.handshakeMs = handshakeMs;
  }
  endpoint;
  WS;
  log;
  lingerMs;
  handshakeMs;
  ws = null;
  acked = null;
  resolveAck = null;
  rejectAck = null;
  keepaliveMs = DEFAULT_KEEPALIVE_MS;
  kaTimer = null;
  lingerTimer = null;
  handshakeTimer = null;
  /**
   * Connection-level holds taken by {@link open} (one per live transport
   * connection). Distinct from subscriptions so the socket survives a moment
   * with zero channels, and so a transport close releases the socket even
   * though it never subscribed anything.
   */
  holds = 0;
  subs = /* @__PURE__ */ new Map();
  /**
   * Take a connection-level hold and resolve once AppSync has acked the
   * handshake — i.e. once this socket is genuinely usable. Lets the transport
   * report `onOpen` on a fact rather than on having been constructed.
   */
  open(token) {
    this.cancelLinger();
    this.holds += 1;
    return this.ensureSocket(token);
  }
  /**
   * Drop a connection-level hold. When nothing is left the socket closes
   * IMMEDIATELY rather than lingering: the linger exists to absorb
   * subscribe→unsubscribe churn WITHIN a live connection (StrictMode, route
   * changes), and reusing a socket across a deliberate connection close is the
   * opposite of what the caller asked for — a reconnect triggered by a wedged
   * socket would land inside the linger window and hand back the very socket it
   * was trying to replace, reconnecting forever without ever changing anything.
   */
  release() {
    if (this.holds > 0) this.holds -= 1;
    if (this.idle()) this.teardown();
  }
  add(sub) {
    this.cancelLinger();
    this.subs.set(sub.id, sub);
    this.ensureSocket(sub.token).then(() => {
      if (this.subs.has(sub.id)) this.start(sub);
    }).catch((error) => {
      if (this.subs.has(sub.id)) {
        this.subs.delete(sub.id);
        sub.observer.error?.(error);
      }
    });
  }
  remove(id) {
    const sub = this.subs.get(id);
    if (!sub) return;
    this.subs.delete(id);
    if (sub.started && this.ws && this.ws.readyState === WS_OPEN) {
      this.send({ id, type: "stop" });
      this.log.debug("stop", { id });
    }
    if (this.idle()) this.scheduleTeardown();
  }
  /** Nothing wants this socket: no subscriptions and no connection holds. */
  idle() {
    return this.subs.size === 0 && this.holds === 0;
  }
  ensureSocket(token) {
    if (this.acked) return this.acked;
    this.acked = new Promise((resolve, reject) => {
      this.resolveAck = resolve;
      this.rejectAck = reject;
    });
    if (this.handshakeMs > 0) {
      this.handshakeTimer = setTimeout(() => {
        this.handshakeTimer = null;
        this.fail(new Error(`AppSync handshake timed out after ${this.handshakeMs}ms`));
      }, this.handshakeMs);
    }
    const realtimeUrl = appSyncRealtimeUrl(this.endpoint);
    this.log.debug("socket open", { url: realtimeUrl });
    const ws = new this.WS(realtimeUrl, ["graphql-ws", authProtocol(this.endpoint, token)]);
    this.ws = ws;
    ws.onopen = () => this.send({ type: "connection_init" });
    ws.onmessage = (event) => this.onFrame(event.data);
    ws.onerror = () => this.fail(new Error("WebSocket error (handshake rejected or network unreachable)"));
    ws.onclose = (event) => {
      const reason = event?.reason ? `, reason=${event.reason}` : "";
      this.fail(new Error(`WebSocket closed (code=${event?.code ?? "?"}${reason})`));
    };
    return this.acked;
  }
  /** The channel name a subscription is bound to (the `name` GraphQL variable). */
  channelOf(sub) {
    const name = sub?.variables?.name;
    return typeof name === "string" ? name : void 0;
  }
  /** Serialized size of a data frame's payload (debug-only; best-effort). */
  frameBytes(payload) {
    try {
      return JSON.stringify(payload)?.length;
    } catch {
      return void 0;
    }
  }
  start(sub) {
    sub.started = true;
    this.log.debug("start", { id: sub.id, channel: this.channelOf(sub) });
    this.send({
      id: sub.id,
      type: "start",
      payload: {
        data: JSON.stringify({ query: sub.query, variables: sub.variables ?? {} }),
        extensions: { authorization: authHeader(this.endpoint, sub.token) }
      }
    });
  }
  onFrame(raw) {
    const frame = this.parse(raw);
    if (!frame) return;
    const type = frame.type;
    if (type === "ka") {
      this.resetKeepalive();
      return;
    }
    if (type === "connection_ack") {
      this.clearHandshakeTimer();
      const ms = frame.payload?.connectionTimeoutMs;
      this.keepaliveMs = typeof ms === "number" && ms > 0 ? ms : DEFAULT_KEEPALIVE_MS;
      this.resetKeepalive();
      this.log.debug("connection_ack", { keepaliveMs: this.keepaliveMs });
      this.resolveAck?.();
      return;
    }
    if (type === "connection_error" || type === "error" && frame.id == null) {
      this.fail(frame.payload ?? new Error("AppSync connection error"));
      return;
    }
    const id = typeof frame.id === "string" ? frame.id : void 0;
    const sub = id ? this.subs.get(id) : void 0;
    this.resetKeepalive();
    switch (type) {
      case "start_ack":
        this.log.debug("subscribed", { id, channel: this.channelOf(sub) });
        return;
      case "data":
        this.log.debug("message", {
          id,
          channel: this.channelOf(sub),
          bytes: isDebugEnabled() ? this.frameBytes(frame.payload) : void 0
        });
        sub?.observer.next(frame.payload);
        return;
      case "error":
        sub?.observer.error?.(new ChannelScopedError(frame.payload ?? "AppSync subscription error"));
        return;
      case "complete":
        sub?.observer.complete?.();
        return;
      default:
        return;
    }
  }
  parse(raw) {
    if (typeof raw !== "string") {
      return raw && typeof raw === "object" ? raw : null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  send(message) {
    if (this.ws && this.ws.readyState === WS_OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }
  resetKeepalive() {
    if (this.kaTimer) clearTimeout(this.kaTimer);
    this.kaTimer = setTimeout(() => {
      this.fail(new Error("AppSync keep-alive timeout"));
    }, this.keepaliveMs);
  }
  /** Close the socket after `lingerMs` if still idle; reused by re-subscribes. */
  scheduleTeardown() {
    this.cancelLinger();
    if (this.lingerMs <= 0) {
      this.teardown();
      return;
    }
    this.lingerTimer = setTimeout(() => {
      this.lingerTimer = null;
      if (this.idle()) this.teardown();
    }, this.lingerMs);
  }
  cancelLinger() {
    if (this.lingerTimer) {
      clearTimeout(this.lingerTimer);
      this.lingerTimer = null;
    }
  }
  clearHandshakeTimer() {
    if (this.handshakeTimer) {
      clearTimeout(this.handshakeTimer);
      this.handshakeTimer = null;
    }
  }
  /** Surface a connection-level failure to every subscriber, then reset. */
  fail(error) {
    this.log[this.subs.size > 0 ? "warn" : "debug"]("socket failed", {
      error: describeError(error)
    });
    this.rejectAck?.(error);
    const subs = Array.from(this.subs.values());
    this.teardown();
    for (const sub of subs) sub.observer.error?.(error);
  }
  /** Close the socket and clear all state (subscriptions are dropped). */
  teardown() {
    this.cancelLinger();
    this.clearHandshakeTimer();
    this.holds = 0;
    if (this.kaTimer) {
      clearTimeout(this.kaTimer);
      this.kaTimer = null;
    }
    const ws = this.ws;
    this.ws = null;
    this.acked = null;
    this.resolveAck = null;
    this.rejectAck = null;
    this.subs.clear();
    if (ws) {
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
      try {
        ws.close();
      } catch {
      }
    }
  }
};
function createAppSyncWebSocketClient(config) {
  const WS = config.webSocketImpl ?? globalThis.WebSocket;
  const doFetch = config.fetchImpl ?? globalThis.fetch?.bind(globalThis);
  const log = config.logger ?? createLogger(STREAMING_PACKAGE).child("appsync-ws");
  const lingerMs = config.socketLingerMs ?? DEFAULT_LINGER_MS;
  const handshakeMs = config.handshakeTimeoutMs ?? STREAM_HANDSHAKE_TIMEOUT_MS;
  const httpTimeoutMs = config.httpTimeoutMs ?? STREAM_HTTP_TIMEOUT_MS;
  const socket = WS ? new RealtimeSocket(config.endpoint, WS, log, lingerMs, handshakeMs) : null;
  const subscriptionClient = {
    graphql(input) {
      const observable = {
        subscribe(observer) {
          if (!socket) {
            observer.error?.(new Error("WebSocket is not available in this environment"));
            return { unsubscribe() {
            } };
          }
          const id = uuid();
          socket.add({
            id,
            query: input.query,
            variables: input.variables,
            token: input.authToken,
            observer,
            started: false
          });
          let active = true;
          return {
            unsubscribe() {
              if (!active) return;
              active = false;
              socket.remove(id);
            }
          };
        }
      };
      return observable;
    }
  };
  const queryClient = {
    async graphql(input) {
      if (!doFetch) throw new Error("fetch is not available in this environment");
      const headers = { "Content-Type": "application/json" };
      if (input.authToken) headers.Authorization = input.authToken;
      const controller = new AbortController();
      const timer = httpTimeoutMs > 0 ? setTimeout(() => controller.abort(), httpTimeoutMs) : null;
      try {
        const response = await doFetch(config.endpoint, {
          method: "POST",
          headers,
          body: JSON.stringify({ query: input.query, variables: input.variables ?? {} }),
          signal: controller.signal
        });
        if (!response.ok) {
          throw new Error(`AppSync HTTP ${response.status}`);
        }
        const json = await response.json();
        if (Array.isArray(json.errors) && json.errors.length > 0) {
          throw new Error(`AppSync GraphQL error: ${JSON.stringify(json.errors)}`);
        }
        return json;
      } finally {
        if (timer) clearTimeout(timer);
      }
    }
  };
  const ready = async (token) => {
    if (!socket) throw new Error("WebSocket is not available in this environment");
    await socket.open(token);
  };
  const release = () => socket?.release();
  return { subscriptionClient, queryClient, ready, release };
}

// src/transport/appsyncClientPool.ts
function createAppSyncClientPool(options = {}) {
  const cache = /* @__PURE__ */ new Map();
  const get = (endpoint) => {
    let entry = cache.get(endpoint);
    if (!entry) {
      entry = createAppSyncWebSocketClient({ endpoint, ...options });
      cache.set(endpoint, entry);
    }
    return entry;
  };
  return {
    resolve: (url) => {
      const { subscriptionClient, queryClient, ready, release } = get(url);
      return {
        client: subscriptionClient,
        ping: createAppSyncPing(queryClient),
        // The transport reports `onOpen` only after this resolves, so a failed
        // handshake is a failed connect attempt (backoff grows, a fallback
        // chain advances) rather than an "open" connection that immediately
        // errors on its first subscription.
        ready,
        release
      };
    },
    publishFor: (url) => createAppSyncPublish(get(url).queryClient)
  };
}

// src/transport/sseTransport.ts
var ES_CLOSED = 2;
function defaultBuildUrl(base, channel, ctx) {
  const url = new URL(base, typeof location !== "undefined" ? location.href : "http://localhost");
  url.searchParams.set("channel", channel.id);
  if (ctx.region) url.searchParams.set("region", ctx.region);
  if (ctx.token) url.searchParams.set("token", ctx.token);
  return url.toString();
}
function defaultParse(data) {
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
}
function resolveFactory(options) {
  if (options.eventSourceFactory) return options.eventSourceFactory;
  const ctor = globalThis.EventSource;
  if (!ctor) {
    throw new Error("SSE transport unavailable: no EventSource in this runtime");
  }
  return (url, init) => new ctor(url, init);
}
function createSseTransport(options = {}) {
  const buildUrl = options.buildUrl ?? defaultBuildUrl;
  const parse = options.parse ?? defaultParse;
  return {
    requiresChannels: true,
    connect(connectOptions, handlers) {
      const factory = resolveFactory(options);
      const sources = /* @__PURE__ */ new Set();
      let opened = false;
      let closed = false;
      let resolveReady;
      let rejectReady;
      const ready = new Promise((resolve, reject) => {
        resolveReady = resolve;
        rejectReady = reject;
      });
      void ready.catch(() => {
      });
      return {
        ready,
        subscribe(channel) {
          if (closed) return () => {
          };
          let source;
          try {
            source = factory(buildUrl(connectOptions.url, channel, connectOptions), {
              withCredentials: options.withCredentials
            });
          } catch (error) {
            rejectReady(error);
            handlers.onError(error);
            return () => {
            };
          }
          const onData = (ev) => {
            if (closed || !sources.has(source)) return;
            handlers.onMessage(channel.id, parse(ev.data ?? ""));
          };
          source.addEventListener("message", onData);
          if (options.eventName) source.addEventListener(options.eventName, onData);
          source.onopen = () => {
            if (closed || !sources.has(source)) return;
            if (!opened) {
              opened = true;
              resolveReady();
              handlers.onOpen?.();
            }
            handlers.onActivity?.();
          };
          source.onerror = (err) => {
            if (closed || !sources.has(source)) return;
            if (source.readyState !== void 0 && source.readyState !== ES_CLOSED) return;
            const error = err ?? new Error(`SSE channel "${channel.id}" error`);
            rejectReady(error);
            handlers.onError(error);
          };
          sources.add(source);
          return () => {
            sources.delete(source);
            source.onopen = source.onerror = null;
            source.close();
          };
        },
        close() {
          if (closed) return;
          closed = true;
          rejectReady(new Error("SSE connection closed"));
          for (const source of sources) {
            source.onopen = source.onerror = null;
            source.close();
          }
          sources.clear();
          handlers.onClose?.();
        }
      };
    }
  };
}

// src/transport/pollTransport.ts
function defaultFetchChannel(ctx) {
  const url = new URL(ctx.url, typeof location !== "undefined" ? location.href : "http://localhost");
  url.searchParams.set("channel", ctx.channel.id);
  if (ctx.region) url.searchParams.set("region", ctx.region);
  const headers = {};
  if (ctx.token) headers.authorization = `Bearer ${ctx.token}`;
  return fetch(url.toString(), { headers, signal: ctx.signal }).then((res) => {
    if (!res.ok) throw new Error(`poll ${ctx.channel.id} failed: ${res.status}`);
    return res.json();
  });
}
function createPollTransport(options = {}) {
  const fetchChannel = options.fetchChannel ?? defaultFetchChannel;
  const intervalMs = options.intervalMs ?? 5e3;
  const timeoutMs = options.timeoutMs ?? intervalMs;
  const immediate = options.immediate ?? true;
  return {
    connect(connectOptions, handlers) {
      handlers.onOpen?.();
      const timers = /* @__PURE__ */ new Set();
      const controllers = /* @__PURE__ */ new Set();
      return {
        subscribe(channel) {
          let poll = 0;
          let inFlight = false;
          let current = null;
          let unsubscribed = false;
          const tick = () => {
            if (inFlight || unsubscribed) return;
            inFlight = true;
            const controller = new AbortController();
            current = controller;
            controllers.add(controller);
            const expiry = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
            const ctx = {
              ...connectOptions,
              channel,
              poll: poll++,
              signal: controller.signal
            };
            Promise.resolve().then(() => fetchChannel(ctx)).then((result) => {
              if (controller.signal.aborted) return;
              handlers.onActivity?.();
              const items = Array.isArray(result) ? result : result == null ? [] : [result];
              for (const item of items) handlers.onMessage(channel.id, item);
            }).catch((error) => {
              if (!controller.signal.aborted) handlers.onError(error);
            }).finally(() => {
              if (expiry) clearTimeout(expiry);
              inFlight = false;
              controllers.delete(controller);
              if (current === controller) current = null;
            });
          };
          const timer = setInterval(tick, intervalMs);
          timers.add(timer);
          if (immediate) tick();
          return () => {
            unsubscribed = true;
            clearInterval(timer);
            timers.delete(timer);
            current?.abort();
            if (current) controllers.delete(current);
            current = null;
          };
        },
        close() {
          for (const timer of timers) clearInterval(timer);
          timers.clear();
          for (const controller of controllers) controller.abort();
          controllers.clear();
          handlers.onClose?.();
        }
      };
    }
  };
}

// src/transport/fallbackTransport.ts
function createFallbackTransport(transports, options = {}) {
  if (transports.length === 0) {
    throw new Error("createFallbackTransport requires at least one transport");
  }
  const establishOn = options.establishOn ?? "open";
  const openTimeoutMs = options.openTimeoutMs ?? 1e4;
  const requiresChannels = transports.some((transport) => transport.requiresChannels);
  return {
    requiresChannels,
    connect(connectOptions, handlers) {
      let earlyConnection;
      let resolveReady;
      let rejectReady;
      const ready = new Promise((resolve, reject) => {
        resolveReady = resolve;
        rejectReady = reject;
      });
      void ready.catch(() => {
      });
      const negotiated = new Promise((resolve, reject) => {
        let index = -1;
        let settled = false;
        let disposed = false;
        let activeConn = null;
        const tracked = /* @__PURE__ */ new Map();
        const unsubs = /* @__PURE__ */ new Map();
        const publicConnection = {
          ready,
          /**
           * Delegate to whichever transport won.
           *
           * This wrapper used to expose no `ping` at all, so composing the
           * documented `[appsync, sse, poll]` chain silently traded the ACTIVE
           * keepalive for the message-silence heartbeat — on the one transport
           * that has a real probe, and precisely in the deployments (proxies,
           * webviews) most likely to wedge a socket open. A getter rather than a
           * fixed property because the active connection is chosen after this
           * object is built, and because a transport with no probe must keep
           * reporting `undefined` rather than a ping that always succeeds.
           */
          get ping() {
            const active = activeConn;
            const probe = active?.ping;
            return probe ? () => probe.call(active) : void 0;
          },
          subscribe(channel) {
            if (disposed) return () => {
            };
            tracked.set(channel.id, channel);
            unsubs.get(channel.id)?.();
            if (activeConn) unsubs.set(channel.id, activeConn.subscribe(channel));
            return () => {
              tracked.delete(channel.id);
              const u = unsubs.get(channel.id);
              unsubs.delete(channel.id);
              u?.();
            };
          },
          close() {
            if (disposed) return;
            disposed = true;
            connectOptions.signal?.removeEventListener("abort", abort);
            cancelAttempt();
            clearUnsubs();
            try {
              activeConn?.close();
            } catch {
            }
            activeConn = null;
            if (!settled) {
              settled = true;
              const error = new Error("fallback connection cancelled");
              rejectReady(error);
              reject(error);
            }
          }
        };
        earlyConnection = publicConnection;
        let cancelAttempt = () => {
        };
        const abort = () => publicConnection.close();
        function clearUnsubs() {
          for (const u of unsubs.values()) {
            try {
              u();
            } catch {
            }
          }
          unsubs.clear();
        }
        function attempt() {
          if (disposed) return;
          index += 1;
          const i = index;
          const transport = transports[i];
          let opened = false;
          let conn = null;
          let established = false;
          let failed = false;
          let readyResolved = true;
          let timer = null;
          cancelAttempt = () => {
            if (timer != null) clearTimeout(timer);
            timer = null;
          };
          const isCurrent = () => i === index && !disposed && !failed;
          const failover = (error) => {
            if (!isCurrent()) return;
            failed = true;
            if (timer != null) {
              clearTimeout(timer);
              timer = null;
            }
            activeConn = null;
            clearUnsubs();
            try {
              conn?.close();
            } catch {
            }
            conn = null;
            if (i + 1 >= transports.length) {
              if (!settled) {
                settled = true;
                disposed = true;
                connectOptions.signal?.removeEventListener("abort", abort);
                rejectReady(error);
                reject(error instanceof Error ? error : new Error(String(error)));
              } else {
                handlers.onError(error);
              }
              return;
            }
            options.onFallback?.({ from: i, to: i + 1, error });
            attempt();
          };
          const finalizeIfReady = () => {
            if (established || !isCurrent()) return;
            if (!conn || !readyResolved) return;
            if (establishOn === "open" && !opened) return;
            established = true;
            if (timer != null) {
              clearTimeout(timer);
              timer = null;
            }
            activeConn = conn;
            if (!settled) {
              settled = true;
              resolveReady();
              resolve(publicConnection);
            }
          };
          const subHandlers = {
            onOpen: () => {
              if (!isCurrent()) return;
              opened = true;
              handlers.onOpen?.();
              finalizeIfReady();
            },
            onMessage: (channel, payload) => {
              if (!isCurrent()) return;
              handlers.onMessage(channel, payload);
            },
            onActivity: () => {
              if (!isCurrent()) return;
              handlers.onActivity?.();
            },
            onError: (error) => {
              if (!isCurrent()) return;
              if (established) handlers.onError(error);
              else failover(error);
            },
            onClose: (reason) => {
              if (!isCurrent()) return;
              if (established) handlers.onClose?.(reason);
              else failover(new Error(reason ?? "transport closed before establishing"));
            }
          };
          if (openTimeoutMs > 0) {
            timer = setTimeout(() => {
              if (!established && isCurrent()) {
                failover(new Error(`transport ${i} did not establish within ${openTimeoutMs}ms`));
              }
            }, openTimeoutMs);
          }
          let result;
          try {
            result = transport.connect(connectOptions, subHandlers);
          } catch (error) {
            failover(error);
            return;
          }
          Promise.resolve(result).then((c) => {
            if (!isCurrent()) {
              try {
                c.close();
              } catch {
              }
              return;
            }
            conn = c;
            activeConn = c;
            if (c.ready) {
              readyResolved = false;
              void c.ready.then(() => {
                readyResolved = true;
                finalizeIfReady();
              }, failover);
            }
            for (const channel of tracked.values()) {
              if (!isCurrent()) break;
              const off = c.subscribe(channel);
              if (isCurrent()) unsubs.set(channel.id, off);
              else off();
            }
            finalizeIfReady();
          }).catch((error) => failover(error));
        }
        if (connectOptions.signal?.aborted) abort();
        else {
          connectOptions.signal?.addEventListener("abort", abort, { once: true });
          attempt();
        }
      });
      if (!requiresChannels) return negotiated;
      void negotiated.catch(() => {
      });
      return earlyConnection;
    }
  };
}

// src/transport/mockTransport.ts
function createMockTransport() {
  let active = null;
  const transport = {
    connect(_options, handlers) {
      const channels = /* @__PURE__ */ new Set();
      active = { handlers, channels };
      handlers.onOpen?.();
      const connection = {
        subscribe(channel) {
          channels.add(channel.id);
          return () => channels.delete(channel.id);
        },
        close() {
          channels.clear();
          if (active?.handlers === handlers) active = null;
          handlers.onClose?.();
        }
      };
      return connection;
    }
  };
  return {
    transport,
    emit(channel, payload) {
      if (active?.channels.has(channel)) active.handlers.onMessage(channel, payload);
    },
    drop(reason) {
      const current = active;
      active = null;
      current?.handlers.onClose?.(reason ?? "mock drop");
    },
    channels() {
      return active ? [...active.channels] : [];
    }
  };
}
function computeBackoff(retries, options = {}) {
  return computeBackoff$1(retries, {
    baseMs: STREAM_BACKOFF_BASE_MS,
    maxMs: STREAM_BACKOFF_MAX_MS,
    factor: STREAM_BACKOFF_FACTOR,
    jitter: STREAM_BACKOFF_JITTER,
    ...options
  });
}
function failoverRegion(current) {
  try {
    return convertRegionToBackupRegion(current);
  } catch {
    return current;
  }
}
function safeAlias(region) {
  try {
    return convertRegionToAlias(region);
  } catch {
    return void 0;
  }
}
function aliasEndpointResolver(template, templateRegion) {
  const templateAlias = safeAlias(templateRegion);
  return (region) => {
    if (!templateAlias) return template;
    const targetAlias = safeAlias(region);
    if (!targetAlias || targetAlias === templateAlias) return template;
    try {
      const url = new URL(template);
      const labels = url.hostname.split(".");
      const at = labels.indexOf(templateAlias);
      if (at === -1) return template;
      labels[at] = targetAlias;
      url.hostname = labels.join(".");
      return url.toString();
    } catch {
      return template;
    }
  };
}

// src/connection/healthcheck.ts
var Heartbeat = class {
  constructor(intervalMs, timeoutMs, onStale) {
    this.intervalMs = intervalMs;
    this.timeoutMs = timeoutMs;
    this.onStale = onStale;
  }
  intervalMs;
  timeoutMs;
  onStale;
  timer = null;
  last = Date.now();
  start() {
    this.last = Date.now();
    this.stop();
    this.timer = setInterval(() => {
      if (Date.now() - this.last > this.timeoutMs) this.onStale();
    }, this.intervalMs);
  }
  /** Record activity — resets the staleness clock. */
  beat() {
    this.last = Date.now();
  }
  stop() {
    if (this.timer != null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
};
var StreamConnection = class {
  constructor(opts) {
    this.opts = opts;
    this.log = (opts.logger ?? createLogger(STREAMING_PACKAGE)).child("connection");
    this.state = { ...initialConnectionState(opts.initialRegion), isLeader: true };
    this.heartbeat = new Heartbeat(
      opts.heartbeatIntervalMs ?? STREAM_HEARTBEAT_INTERVAL_MS,
      opts.heartbeatTimeoutMs ?? STREAM_HEARTBEAT_TIMEOUT_MS,
      () => this.onStale()
    );
  }
  opts;
  log;
  heartbeat;
  desired = /* @__PURE__ */ new Map();
  state;
  conn = null;
  reconnectTimer = null;
  pingTimer = null;
  failureStreak = 0;
  started = false;
  connecting = false;
  connectAbort = null;
  /** Bumped on every (re)connect + teardown so stale async callbacks are ignored. */
  generation = 0;
  get current() {
    return this.state;
  }
  start() {
    if (this.started) return;
    this.started = true;
    void this.open();
  }
  stop() {
    if (!this.started) return;
    this.started = false;
    this.clearReconnect();
    this.teardownConnection();
    this.failureStreak = 0;
    this.setState({ status: ConnectionStatus.CLOSED, retries: 0 });
  }
  /**
   * Manually move the live connection to `region` (the backup, or back to the
   * primary). Resets the failure streak and immediately reconnects onto the new
   * region's endpoint. No-op if already there or not started. Used for
   * non-automatic failover (`autoRegionFailover: false`) and operator tooling.
   */
  setRegion(region) {
    if (!region || region === this.state.region) return;
    this.log.info("region switch (manual)", { from: this.state.region, to: region });
    this.clearReconnect();
    this.teardownConnection();
    this.failureStreak = 0;
    this.setState({ region, status: ConnectionStatus.RECONNECTING, retries: 0 });
    if (this.started) void this.open();
  }
  addChannel(descriptor) {
    const existing = this.desired.get(descriptor.id);
    if (existing) {
      if (sameDescriptor(existing.descriptor, descriptor)) return;
      existing.descriptor = descriptor;
      if (this.conn) {
        this.log.debug("resubscribe (descriptor changed)", { channel: descriptor.id });
        existing.unsubscribe?.();
        existing.unsubscribe = this.conn.subscribe(descriptor);
      }
      return;
    }
    const entry = { descriptor };
    this.desired.set(descriptor.id, entry);
    if (this.conn) {
      this.log.debug("subscribe", { channel: descriptor.id });
      entry.unsubscribe = this.conn.subscribe(descriptor);
      this.logChannels("channel added");
    } else {
      this.log.debug("subscribe (deferred \u2014 not open)", { channel: descriptor.id });
      if (this.started && this.reconnectTimer == null) void this.open();
    }
  }
  removeChannel(id) {
    const entry = this.desired.get(id);
    if (!entry) return;
    entry.unsubscribe?.();
    this.desired.delete(id);
    if (this.started && this.desired.size === 0 && this.opts.transport.requiresChannels) {
      this.clearReconnect();
      this.teardownConnection();
      this.failureStreak = 0;
      this.setState({ status: ConnectionStatus.IDLE, retries: 0, lastError: void 0 });
      return;
    }
    if (this.conn && this.state.status === ConnectionStatus.OPEN) {
      this.logChannels("channel removed");
    }
  }
  /** Info snapshot of the live channel set (mirrors the `open` log) so the active
   *  total stays visible as channels are added/removed after the connection opens. */
  logChannels(message) {
    this.log.info(message, {
      channels: this.desired.size,
      ids: Array.from(this.desired.keys())
    });
  }
  setState(patch) {
    this.state = { ...this.state, ...patch };
    this.opts.onState?.(this.state);
  }
  async open() {
    if (!this.started || this.connecting || this.conn) return;
    if (this.opts.transport.requiresChannels && this.desired.size === 0) return;
    this.connecting = true;
    const generation = ++this.generation;
    const controller = new AbortController();
    this.connectAbort = controller;
    const deadline = Date.now() + (this.opts.connectTimeoutMs ?? STREAM_CONNECT_TIMEOUT_MS);
    this.setState({
      status: this.failureStreak > 0 ? ConnectionStatus.RECONNECTING : ConnectionStatus.CONNECTING
    });
    try {
      const conn = await withTimeout(
        (async () => {
          const token = await this.opts.getToken?.();
          if (controller.signal.aborted) throw new Error("connect cancelled");
          const url = this.opts.endpoint(this.state.region);
          this.log.debug("connecting", { region: this.state.region, url });
          return this.opts.transport.connect(
            { url, region: this.state.region, token, signal: controller.signal },
            {
              onOpen: () => this.log.debug("transport open"),
              onMessage: (channel, payload) => this.handleMessage(generation, channel, payload),
              // Proof of life that is not an app message. Without it the
              // silence watchdog cannot tell a healthy IDLE connection from a
              // wedged one, and tore down a perfectly good low-traffic feed
              // every `heartbeatTimeoutMs` — three of those in a row also trip
              // automatic region failover.
              onActivity: () => this.handleActivity(generation),
              onError: (error) => this.failAndReconnect(generation, `transport error: ${describeError(error)}`),
              onClose: (reason) => this.failAndReconnect(generation, reason ?? "transport closed")
            }
          );
        })(),
        this.opts.connectTimeoutMs ?? STREAM_CONNECT_TIMEOUT_MS,
        controller.signal,
        (late) => {
          try {
            late.close();
          } catch (error) {
            this.log.debug("late close threw", { error: describeError(error) });
          }
        }
      );
      if (!this.started || generation !== this.generation) {
        conn.close();
        return;
      }
      this.conn = conn;
      for (const entry of this.desired.values()) {
        if (generation !== this.generation) return;
        entry.unsubscribe = conn.subscribe(entry.descriptor);
      }
      if (conn.ready) {
        await withTimeout(conn.ready, Math.max(0, deadline - Date.now()), controller.signal);
      }
      if (!this.started || generation !== this.generation) return;
      this.openSucceeded();
    } catch (error) {
      this.failAndReconnect(generation, `connect threw: ${describeError(error)}`);
    } finally {
      if (generation === this.generation) this.connecting = false;
    }
  }
  openSucceeded() {
    this.failureStreak = 0;
    this.heartbeat.start();
    this.startPing();
    this.setState({ status: ConnectionStatus.OPEN, retries: 0, lastError: void 0 });
    this.log.info("open", {
      region: this.state.region,
      url: this.opts.endpoint(this.state.region),
      channels: this.desired.size,
      ids: Array.from(this.desired.keys())
    });
  }
  /**
   * Active keepalive: when the transport exposes `ping`, poll it so an
   * idle-but-wedged socket is caught even with zero real traffic. A successful
   * ping refreshes the heartbeat; a failed/timed-out one forces a reconnect.
   */
  startPing() {
    this.stopPing();
    if (!this.conn?.ping) return;
    const interval = this.opts.pingIntervalMs ?? STREAM_PING_INTERVAL_MS;
    const generation = this.generation;
    this.pingTimer = setInterval(() => void this.runPing(generation), interval);
  }
  stopPing() {
    if (this.pingTimer != null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
  async runPing(generation) {
    const conn = this.conn;
    if (!conn?.ping || generation !== this.generation) return;
    const timeoutMs = this.opts.pingTimeoutMs ?? STREAM_PING_TIMEOUT_MS;
    try {
      await withTimeout(conn.ping(), timeoutMs);
      if (generation !== this.generation) return;
      this.heartbeat.beat();
      this.log.debug("ping ok", { region: this.state.region });
    } catch (error) {
      this.failAndReconnect(generation, `ping failed: ${describeError(error)}`);
    }
  }
  /**
   * Transport-level liveness that isn't an app message (a successful poll, a
   * server keep-alive). Resets the silence watchdog without touching
   * `lastEventAt`, which means "when did the app last receive data".
   */
  handleActivity(generation) {
    if (generation !== this.generation) return;
    this.heartbeat.beat();
  }
  handleMessage(generation, channel, payload) {
    if (generation !== this.generation) return;
    this.heartbeat.beat();
    this.setState({ lastEventAt: Date.now() });
    this.log.debug("message", { channel, payload });
    this.opts.onMessage(channel, payload);
  }
  onStale() {
    this.log.warn("healthcheck stale \u2014 forcing reconnect", { region: this.state.region });
    this.setState({ status: ConnectionStatus.DEGRADED });
    this.failAndReconnect(this.generation, "healthcheck stale");
  }
  failAndReconnect(generation, reason) {
    if (!this.started || generation !== this.generation) return;
    this.teardownConnection();
    this.failureStreak += 1;
    const threshold = this.opts.regionFailoverThreshold ?? STREAM_REGION_FAILOVER_THRESHOLD;
    let region = this.state.region;
    if (this.opts.autoRegionFailover !== false && this.failureStreak % threshold === 0) {
      region = failoverRegion(region);
      if (region !== this.state.region) {
        this.log.warn("region failover (auto)", { from: this.state.region, to: region });
      }
    }
    const delay = computeBackoff(this.failureStreak - 1, this.opts.backoff);
    this.log.warn("reconnecting", { reason, delayMs: delay, attempt: this.failureStreak });
    this.setState({
      status: ConnectionStatus.RECONNECTING,
      region,
      retries: this.failureStreak,
      lastError: reason
    });
    this.clearReconnect();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.open();
    }, delay);
  }
  teardownConnection() {
    this.generation += 1;
    this.connecting = false;
    const controller = this.connectAbort;
    this.connectAbort = null;
    controller?.abort();
    this.heartbeat.stop();
    this.stopPing();
    for (const entry of this.desired.values()) {
      entry.unsubscribe?.();
      entry.unsubscribe = void 0;
    }
    if (this.conn) {
      try {
        this.conn.close();
      } catch (error) {
        this.log.debug("close threw", { error: describeError(error) });
      }
      this.conn = null;
    }
  }
  clearReconnect() {
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
};
function sameDescriptor(a, b) {
  if (a === b) return true;
  if (a.query !== b.query) return false;
  return stableKey(a.variables) === stableKey(b.variables);
}
function stableKey(value) {
  if (!value) return "";
  try {
    return stableStringify(value, /* @__PURE__ */ new Set());
  } catch {
    return Math.random().toString(36);
  }
}
function stableStringify(value, seen) {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "null";
  }
  if (seen.has(value)) throw new TypeError("cyclic variables");
  seen.add(value);
  try {
    if (Array.isArray(value)) {
      return `[${value.map((item) => stableStringify(item, seen)).join(",")}]`;
    }
    const entries = Object.entries(value).filter(([, item]) => item !== void 0).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
    return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item, seen)}`).join(",")}}`;
  } finally {
    seen.delete(value);
  }
}
function withTimeout(promise, ms, signal, onLateResolve) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = () => {
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
    };
    const abort = () => {
      finish();
      reject(new Error("connect cancelled"));
    };
    const timer = setTimeout(() => {
      finish();
      reject(new Error(`timed out after ${ms}ms`));
    }, ms);
    if (signal?.aborted) abort();
    else signal?.addEventListener("abort", abort, { once: true });
    promise.then(
      (value) => {
        if (settled) {
          onLateResolve?.(value);
          return;
        }
        finish();
        resolve(value);
      },
      (error) => {
        if (settled) return;
        finish();
        reject(error);
      }
    );
  });
}

// src/dedupe/dedupe.ts
var Deduper = class {
  constructor(capacity = STREAM_DEDUPE_CAPACITY) {
    this.capacity = capacity;
  }
  capacity;
  /** Insertion-ordered map doubles as the LRU recency queue. */
  seen = /* @__PURE__ */ new Set();
  /**
   * Record `key` and report whether it's new. A null key is never de-duplicated
   * (caller couldn't derive an id) — treated as new so nothing is dropped.
   */
  accept(key) {
    if (key == null) return true;
    if (this.seen.has(key)) {
      this.seen.delete(key);
      this.seen.add(key);
      return false;
    }
    this.seen.add(key);
    if (this.seen.size > this.capacity) {
      const oldest = this.seen.values().next().value;
      if (oldest !== void 0) this.seen.delete(oldest);
    }
    return true;
  }
  has(key) {
    return this.seen.has(key);
  }
  clear() {
    this.seen.clear();
  }
  get size() {
    return this.seen.size;
  }
};
var EnrichPipeline = class {
  enrichers = [];
  log;
  constructor(logger) {
    this.log = (logger ?? createLogger(STREAMING_PACKAGE)).child("enrich");
  }
  /** Register an enricher; returns an unregister fn. */
  use(enricher) {
    this.enrichers.push(enricher);
    return () => {
      const index = this.enrichers.indexOf(enricher);
      if (index >= 0) this.enrichers.splice(index, 1);
    };
  }
  /** Apply all enrichers in order. A throwing enricher is skipped. */
  run(payload, context) {
    let current = payload;
    for (const enricher of this.enrichers) {
      try {
        current = enricher(current, context);
      } catch (error) {
        this.log.warn("enricher threw \u2014 skipped", { channel: context.channel, error: String(error) });
      }
    }
    return current;
  }
  get size() {
    return this.enrichers.length;
  }
};
function makeTabId() {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${rand}`;
}
var LeaderElection = class {
  constructor(opts) {
    this.opts = opts;
    this.heartbeatMs = opts.heartbeatMs ?? STREAM_LEADER_HEARTBEAT_MS;
    this.staleMs = opts.staleMs ?? STREAM_LEADER_STALE_MS;
    this.supported = typeof BroadcastChannel !== "undefined";
    this.log = (opts.logger ?? createLogger(STREAMING_PACKAGE)).child("leader");
  }
  opts;
  /** This tab's unique, time-sortable id. */
  id = makeTabId();
  heartbeatMs;
  staleMs;
  supported;
  log;
  peers = /* @__PURE__ */ new Map();
  onPageHide = () => this.announceBye();
  bc = null;
  timer = null;
  isLeader = false;
  get leader() {
    return this.isLeader;
  }
  /** Live tabs participating in the election, including this one. */
  get tabCount() {
    return this.supported ? this.peers.size : 1;
  }
  /** Other live tabs — i.e. followers when this tab is the leader. */
  get followerCount() {
    return Math.max(0, this.tabCount - 1);
  }
  start() {
    if (!this.supported) {
      this.isLeader = true;
      this.log.debug("BroadcastChannel unavailable \u2014 single-tab leader");
      this.opts.onChange(true);
      return;
    }
    this.bc = new BroadcastChannel(this.opts.channelName ?? STREAM_ELECTION_CHANNEL);
    this.bc.onmessage = (event) => this.onMessage(event.data);
    if (typeof addEventListener !== "undefined") addEventListener("pagehide", this.onPageHide);
    this.peers.set(this.id, Date.now());
    this.beat();
    this.timer = setInterval(() => this.tick(), this.heartbeatMs);
    this.reevaluate();
  }
  stop() {
    if (this.timer != null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (typeof removeEventListener !== "undefined") removeEventListener("pagehide", this.onPageHide);
    this.announceBye();
    this.bc?.close();
    this.bc = null;
    this.peers.clear();
    this.isLeader = false;
  }
  beat() {
    const now = Date.now();
    this.peers.set(this.id, now);
    this.bc?.postMessage({ type: "heartbeat", id: this.id, ts: now });
  }
  announceBye() {
    this.peers.delete(this.id);
    this.bc?.postMessage({ type: "bye", id: this.id });
  }
  onMessage(message) {
    if (!message || typeof message !== "object") return;
    if (message.type === "heartbeat") {
      const known = this.peers.has(message.id);
      this.peers.set(message.id, Date.now());
      if (!known && message.id !== this.id) this.beat();
    } else if (message.type === "bye") {
      this.peers.delete(message.id);
    } else {
      return;
    }
    this.reevaluate();
  }
  tick() {
    this.beat();
    const cutoff = Date.now() - this.staleMs;
    for (const [id, last] of this.peers) {
      if (id !== this.id && last < cutoff) this.peers.delete(id);
    }
    this.reevaluate();
  }
  reevaluate() {
    let lowest = this.id;
    for (const id of this.peers.keys()) {
      if (id < lowest) lowest = id;
    }
    const shouldLead = lowest === this.id;
    if (shouldLead === this.isLeader) return;
    this.isLeader = shouldLead;
    if (shouldLead) {
      this.log.info("became leader", { id: this.id, tabs: this.tabCount, followers: this.followerCount });
    } else {
      this.log.info("follower \u2014 another tab is leader", {
        id: this.id,
        leader: lowest,
        peers: this.peers.size
      });
    }
    this.opts.onChange(shouldLead);
  }
};

// src/sharing/crossTabBus.ts
var CrossTabBus = class {
  constructor(channelName = STREAM_EVENTS_CHANNEL) {
    this.channelName = channelName;
  }
  channelName;
  supported = typeof BroadcastChannel !== "undefined";
  handlers = /* @__PURE__ */ new Set();
  bc = null;
  start() {
    if (!this.supported || this.bc) return;
    this.bc = new BroadcastChannel(this.channelName);
    this.bc.onmessage = (event) => {
      const message = event.data;
      if (!message || typeof message !== "object") return;
      for (const handler of this.handlers) {
        try {
          handler(message);
        } catch {
        }
      }
    };
  }
  publish(message) {
    this.bc?.postMessage(message);
  }
  subscribe(handler) {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }
  stop() {
    this.bc?.close();
    this.bc = null;
    this.handlers.clear();
  }
};
var StreamingClient = class {
  constructor(opts) {
    this.opts = opts;
    this.log = (opts.logger ?? createLogger(STREAMING_PACKAGE)).child("client");
    this.dedupeKey = opts.dedupeKey ?? autheDedupeKey;
    this.deduper = new Deduper(opts.dedupeCapacity ?? STREAM_DEDUPE_CAPACITY);
    this.enrich = new EnrichPipeline(this.log);
    this.shareAcrossTabs = opts.shareAcrossTabs ?? true;
    this.sharingScope = encodeURIComponent(JSON.stringify([
      opts.endpoint(opts.region),
      opts.region,
      opts.sharingKey ?? ""
    ]));
    this.regionKey = `${STREAM_REGION_KEY}:${this.sharingScope}`;
    for (const enricher of opts.enrichers ?? []) this.enrich.use(enricher);
    for (const channel of opts.channels ?? []) {
      this.channels.set(channel.id, channel);
      this.staticChannels.add(channel.id);
    }
    const region = this.resolveInitialRegion();
    this.state = initialConnectionState(region);
  }
  opts;
  log;
  deduper;
  enrich;
  dedupeKey;
  channels = /* @__PURE__ */ new Map();
  /** Upfront and explicit addChannel declarations, retained until removeChannel. */
  staticChannels = /* @__PURE__ */ new Set();
  /** Active `subscribeChannel` count per channel, for demand-driven teardown. */
  channelRefs = /* @__PURE__ */ new Map();
  /** Each peer renews its complete demand; expired peers cannot retain channels. */
  foreignDemand = /* @__PURE__ */ new Map();
  connectionChannels = /* @__PURE__ */ new Set();
  handlers = /* @__PURE__ */ new Map();
  stateListeners = /* @__PURE__ */ new Set();
  shareAcrossTabs;
  sharingScope;
  regionKey;
  demandTimer = null;
  onPageHide = () => this.releaseDemand();
  election = null;
  bus = null;
  connection = null;
  state;
  started = false;
  // -- lifecycle ------------------------------------------------------------
  start() {
    if (this.started) return;
    this.started = true;
    if (this.shareAcrossTabs) {
      const scope = `${STREAM_BROADCAST_CHANNEL}.v2.${this.sharingScope}`;
      this.election = new LeaderElection({
        channelName: `${scope}.election`,
        logger: this.log,
        onChange: (isLeader) => this.onLeadershipChange(isLeader)
      });
      this.bus = new CrossTabBus(`${scope}.events`);
      this.bus.start();
      this.bus.subscribe((message) => this.onBusMessage(message));
      this.election.start();
      this.publishDemand();
      this.bus.publish({ kind: "hello" });
      this.demandTimer = setInterval(() => {
        this.publishDemand();
        const cutoff = Date.now() - STREAM_LEADER_STALE_MS;
        let removed = false;
        for (const [owner, demand] of this.foreignDemand) {
          if (demand.seenAt < cutoff) {
            this.foreignDemand.delete(owner);
            removed = true;
          }
        }
        if (removed) this.syncChannels();
      }, STREAM_LEADER_HEARTBEAT_MS);
      if (typeof addEventListener !== "undefined") addEventListener("pagehide", this.onPageHide);
    } else {
      this.onLeadershipChange(true);
    }
  }
  stop() {
    if (!this.started) return;
    this.started = false;
    this.releaseDemand();
    if (this.demandTimer != null) clearInterval(this.demandTimer);
    this.demandTimer = null;
    if (typeof removeEventListener !== "undefined") removeEventListener("pagehide", this.onPageHide);
    this.election?.stop();
    this.election = null;
    const connection = this.connection;
    this.connection = null;
    connection?.stop();
    this.connectionChannels.clear();
    this.bus?.stop();
    this.bus = null;
    this.foreignDemand.clear();
    this.setState({ status: ConnectionStatus.CLOSED, isLeader: false });
  }
  /** Alias for `stop()` — frees all timers, sockets, and BroadcastChannels. */
  destroy() {
    this.stop();
  }
  // -- subscriptions --------------------------------------------------------
  /**
   * Attach a handler to a channel id (use `"*"` / `ALL_CHANNELS` for every
   * channel). Returns an unsubscribe fn. Channel *demand* (the GraphQL doc +
   * variables) is declared separately via `addChannel`/the `channels` option.
   */
  subscribe(channelId, handler) {
    let set = this.handlers.get(channelId);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this.handlers.set(channelId, set);
    }
    set.add(handler);
    return () => {
      const current = this.handlers.get(channelId);
      if (!current) return;
      current.delete(handler);
      if (current.size === 0) this.handlers.delete(channelId);
    };
  }
  /**
   * Dynamically subscribe to a *new* channel: declares the channel on the
   * shared connection (or forwards demand to the leader) AND attaches a handler
   * in one call. Ref-counted — when the last dynamic subscriber for a channel
   * unsubscribes, the channel is removed from the connection (channels declared
   * via the `channels` option are kept). Returns an unsubscribe fn.
   */
  subscribeChannel(descriptor, handler) {
    this.channelRefs.set(descriptor.id, (this.channelRefs.get(descriptor.id) ?? 0) + 1);
    const off = this.subscribe(descriptor.id, handler);
    this.channels.set(descriptor.id, descriptor);
    this.demandChanged();
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      off();
      const remaining = (this.channelRefs.get(descriptor.id) ?? 1) - 1;
      if (remaining > 0) {
        this.channelRefs.set(descriptor.id, remaining);
        return;
      }
      this.channelRefs.delete(descriptor.id);
      if (!this.staticChannels.has(descriptor.id)) {
        this.channels.delete(descriptor.id);
        this.demandChanged();
      }
    };
  }
  /** Declare a channel to subscribe on the shared connection. */
  addChannel(descriptor) {
    this.staticChannels.add(descriptor.id);
    this.channels.set(descriptor.id, descriptor);
    this.demandChanged();
  }
  /** Release this tab's explicit declaration; live dynamic/foreign demand survives. */
  removeChannel(channelId) {
    this.staticChannels.delete(channelId);
    if (!this.channelRefs.has(channelId)) this.channels.delete(channelId);
    this.demandChanged();
  }
  /** Register an enricher applied to every event; returns an unregister fn. */
  use(enricher) {
    return this.enrich.use(enricher);
  }
  /**
   * Manually switch the active region (primary ↔ backup) and persist it so a
   * reload starts there. The leader tab reconnects its socket onto the new
   * region immediately; follower tabs adopt it when they next become leader.
   * Pairs with `autoRegionFailover: false` for fully operator-driven failover.
   */
  setRegion(region) {
    if (!region || region === this.state.region) return;
    writeLocalJson(this.regionKey, region);
    this.setState({ region });
    if (this.isLeader()) this.connection?.setRegion(region);
  }
  /** The region the shared connection is currently using. */
  getRegion() {
    return this.state.region;
  }
  /** Other live tabs sharing this client (followers when this tab leads). */
  followerCount() {
    return this.election?.followerCount ?? 0;
  }
  /** Live tabs (including this one) sharing this client. */
  tabCount() {
    return this.election?.tabCount ?? 1;
  }
  /**
   * A structured snapshot of the live streaming state — region, leadership,
   * tab/follower counts, declared channels, per-channel subscriber counts and
   * the dedupe window size. Handy from the console:
   * `window.__autheStreaming.stats()`.
   */
  snapshot() {
    const handlers = {};
    for (const [id, set] of this.handlers) handlers[id] = set.size;
    return {
      region: this.state.region,
      status: this.state.status,
      isLeader: this.isLeader(),
      tabId: this.election?.id ?? null,
      tabs: this.tabCount(),
      followers: this.followerCount(),
      channels: [...this.desiredChannels().keys()],
      handlers,
      dedupeSeen: this.deduper.size
    };
  }
  // -- state ----------------------------------------------------------------
  getState() {
    return this.state;
  }
  onState(listener) {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }
  isLeader() {
    return this.election ? this.election.leader : this.started;
  }
  // -- internals ------------------------------------------------------------
  resolveInitialRegion() {
    const persisted = readLocalJson(this.regionKey, null);
    if (persisted) {
      try {
        if (isRegionEnabled(persisted)) return persisted;
      } catch {
      }
    }
    return this.opts.region;
  }
  onLeadershipChange(isLeader) {
    if (isLeader) {
      this.foreignDemand.clear();
      this.setState({ isLeader: true });
      this.startConnection();
      this.bus?.publish({ kind: "resync" });
      return;
    }
    const connection = this.connection;
    this.connection = null;
    this.connectionChannels.clear();
    this.foreignDemand.clear();
    connection?.stop();
    this.setState({
      isLeader: false,
      status: ConnectionStatus.IDLE,
      retries: 0,
      lastError: void 0
    });
    this.bus?.publish({ kind: "hello" });
    this.publishDemand();
  }
  startConnection() {
    if (this.connection) return;
    const connection = new StreamConnection({
      transport: this.opts.transport,
      endpoint: this.opts.endpoint,
      initialRegion: this.state.region,
      getToken: this.opts.getToken,
      backoff: this.opts.backoff,
      heartbeatIntervalMs: this.opts.heartbeatIntervalMs,
      heartbeatTimeoutMs: this.opts.heartbeatTimeoutMs,
      pingIntervalMs: this.opts.pingIntervalMs,
      pingTimeoutMs: this.opts.pingTimeoutMs,
      connectTimeoutMs: this.opts.connectTimeoutMs,
      regionFailoverThreshold: this.opts.regionFailoverThreshold,
      autoRegionFailover: this.opts.autoRegionFailover,
      logger: this.log,
      onMessage: (channel, payload) => this.onConnectionMessage(channel, payload),
      onState: (state) => this.onConnectionState(connection, state)
    });
    this.connection = connection;
    this.syncChannels();
    connection.start();
  }
  /** Approx serialized size of a payload (bytes) for debug logs; undefined if unserializable. */
  bytesOf(payload) {
    try {
      const json = JSON.stringify(payload);
      return json == null ? void 0 : json.length;
    } catch {
      return void 0;
    }
  }
  /**
   * Leader: a fresh message off the wire.
   *
   * The backend may coalesce several rows into one message for a channel, so
   * expand first and run the per-event path once per item. Everything
   * downstream (dedupe, enrich, handlers, cross-tab broadcast) therefore sees
   * individual events and never has to know about batching.
   */
  onConnectionMessage(channel, payload) {
    for (const item of unwrapStreamBatch(payload)) {
      this.onConnectionEvent(channel, item);
    }
  }
  /** Leader: one event off the wire. Dedupe → enrich → deliver + broadcast. */
  onConnectionEvent(channel, payload) {
    const key = this.dedupeKey(payload, channel);
    this.log.debug("received", {
      channel,
      key,
      dedupe: key == null ? "unkeyed" : "checking",
      region: this.state.region,
      bytes: this.bytesOf(payload),
      followers: this.followerCount()
    });
    if (!this.deduper.accept(key)) {
      this.log.debug("dropped duplicate", { channel, key, seen: this.deduper.size, region: this.state.region });
      return;
    }
    const enriched = this.enrich.run(payload, { channel });
    const message = {
      channel,
      payload: enriched,
      dedupeKey: key,
      receivedAt: Date.now(),
      origin: "local"
    };
    this.deliver(message);
    this.bus?.publish({ kind: "event", message });
  }
  onConnectionState(connection, state) {
    if (this.connection !== connection) return;
    if (state.region !== this.state.region) {
      writeLocalJson(this.regionKey, state.region);
    }
    const isLeader = this.isLeader();
    this.setState({ ...state, isLeader });
    this.bus?.publish({ kind: "state", state: { ...state, isLeader } });
  }
  onBusMessage(message) {
    switch (message.kind) {
      case "event": {
        const accepted = this.deduper.accept(message.message.dedupeKey);
        this.log.debug("received (broadcast)", {
          channel: message.message.channel,
          key: message.message.dedupeKey,
          accepted
        });
        if (!accepted) return;
        this.deliver({ ...message.message, origin: "broadcast" });
        return;
      }
      case "state": {
        if (!this.isLeader()) this.setState({ ...message.state, isLeader: false });
        return;
      }
      case "demand": {
        if (this.isLeader() && message.owner !== this.election?.id) {
          this.foreignDemand.set(message.owner, {
            descriptors: message.descriptors,
            seenAt: Date.now()
          });
          this.syncChannels();
        }
        return;
      }
      case "leave": {
        if (this.foreignDemand.delete(message.owner)) this.syncChannels();
        return;
      }
      case "hello": {
        if (this.isLeader()) this.bus?.publish({ kind: "state", state: this.state });
        return;
      }
      case "resync": {
        this.publishDemand();
        return;
      }
    }
  }
  publishDemand() {
    if (!this.election) return;
    this.bus?.publish({
      kind: "demand",
      owner: this.election.id,
      descriptors: [...this.channels.values()]
    });
  }
  releaseDemand() {
    if (this.election) this.bus?.publish({ kind: "leave", owner: this.election.id });
  }
  demandChanged() {
    this.syncChannels();
    this.publishDemand();
  }
  desiredChannels() {
    const desired = /* @__PURE__ */ new Map();
    for (const demand of this.foreignDemand.values()) {
      for (const descriptor of demand.descriptors) desired.set(descriptor.id, descriptor);
    }
    for (const descriptor of this.channels.values()) desired.set(descriptor.id, descriptor);
    return desired;
  }
  syncChannels() {
    if (!this.connection) return;
    const desired = this.desiredChannels();
    for (const id of this.connectionChannels) {
      if (!desired.has(id)) {
        this.connection.removeChannel(id);
        this.connectionChannels.delete(id);
      }
    }
    for (const descriptor of desired.values()) {
      this.connection.addChannel(descriptor);
      this.connectionChannels.add(descriptor.id);
    }
  }
  deliver(message) {
    const direct = this.handlers.get(message.channel)?.size ?? 0;
    const wildcard = this.handlers.get(ALL_CHANNELS)?.size ?? 0;
    const handlers = direct + wildcard;
    this.log.debug("deliver", {
      channel: message.channel,
      handlers,
      origin: message.origin,
      region: this.state.region,
      followers: this.followerCount(),
      ...handlers === 0 ? { subscribed: [...this.handlers.keys()] } : {}
    });
    this.fanOut(this.handlers.get(message.channel), message);
    this.fanOut(this.handlers.get(ALL_CHANNELS), message);
  }
  fanOut(set, message) {
    if (!set) return;
    for (const handler of set) {
      try {
        handler(message);
      } catch (error) {
        this.log.warn("subscriber threw", { channel: message.channel, error: String(error) });
      }
    }
  }
  setState(patch) {
    this.state = { ...this.state, ...patch };
    for (const listener of this.stateListeners) {
      try {
        listener(this.state);
      } catch {
      }
    }
  }
};

// src/client/createStreamingClient.ts
function createStreamingClient(options) {
  return new StreamingClient(options);
}
var StreamingContext = React5.createContext(null);
function useStreamingClient() {
  const client = React5.useContext(StreamingContext);
  if (!client) {
    throw new Error("useStreamingClient must be used within a <StreamingProvider>.");
  }
  return client;
}
function useOptionalStreamingClient() {
  return React5.useContext(StreamingContext);
}
function StreamingProvider({
  client,
  autoStart = true,
  stopOnUnmount = false,
  children
}) {
  React5.useEffect(() => {
    if (autoStart) client.start();
    return () => {
      if (stopOnUnmount) client.stop();
    };
  }, [client, autoStart, stopOnUnmount]);
  return /* @__PURE__ */ jsx(StreamingContext.Provider, { value: client, children });
}
function parseChainTip(payload) {
  if (!payload || typeof payload !== "object") return null;
  const record2 = payload;
  const chainId = String(record2.chain_id ?? "").toLowerCase();
  const headBlock = Number(record2.head_block);
  if (!chainId || !Number.isFinite(headBlock) || headBlock <= 0) return null;
  const tip = { chainId, headBlock };
  const confirmedBlock = Number(record2.confirmed_block);
  if (Number.isFinite(confirmedBlock)) tip.confirmedBlock = confirmedBlock;
  if (typeof record2.updated_at === "string") tip.updatedAt = record2.updated_at;
  return tip;
}
function useChainTips(options = {}) {
  const { enabled = true } = options;
  const client = useOptionalStreamingClient();
  const [tips, setTips] = React5.useState({});
  React5.useEffect(() => {
    if (!client || !enabled) return;
    const descriptor = subscribeDescriptor(streamChannelId({}, STREAM_CHAIN_TIPS));
    const handler = (message) => {
      const tip = parseChainTip(message.payload);
      if (!tip) return;
      setTips((current) => {
        const previous = current[tip.chainId];
        if (previous && previous.headBlock >= tip.headBlock) return current;
        return { ...current, [tip.chainId]: tip };
      });
    };
    return client.subscribeChannel(descriptor, handler);
  }, [client, enabled]);
  return tips;
}
function headForChain(tips, chainId) {
  if (!chainId) return null;
  return tips[chainId.toLowerCase()]?.headBlock ?? null;
}
function useConnectionStatus() {
  const client = useStreamingClient();
  return React5.useSyncExternalStore(
    React5.useCallback((onChange) => client.onState(onChange), [client]),
    () => client.getState(),
    () => client.getState()
  );
}
function useStream(channel, options = {}) {
  const { onMessage, enabled = true } = options;
  const client = useStreamingClient();
  const [last, setLast] = React5.useState(null);
  const onMessageRef = React5.useRef(onMessage);
  React5.useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  const descriptor = typeof channel === "string" ? null : channel;
  const channelId = typeof channel === "string" ? channel : channel.id;
  const descriptorKey = descriptor ? JSON.stringify(descriptor) : channelId;
  const [seen, setSeen] = React5.useState(descriptorKey);
  if (seen !== descriptorKey) {
    setSeen(descriptorKey);
    setLast(null);
  }
  React5.useEffect(() => {
    if (!enabled) return;
    const handler = (message) => {
      setLast(message);
      onMessageRef.current?.(message);
    };
    return descriptor ? client.subscribeChannel(descriptor, handler) : client.subscribe(channelId, handler);
  }, [client, channelId, descriptorKey, enabled]);
  return last;
}
var useAppSyncSubscription = useStream;

export { ALL_CHANNELS, ChannelScopedError, ConnectionStatus, CrossTabBus, Deduper, EnrichPipeline, Heartbeat, KNOWN_STREAMS, LeaderElection, PING_QUERY, PUBLISH_MUTATION, STREAMING_PACKAGE, STREAM_ADDRESS_WATCH, STREAM_BACKOFF_BASE_MS, STREAM_BACKOFF_FACTOR, STREAM_BACKOFF_JITTER, STREAM_BACKOFF_MAX_MS, STREAM_BROADCAST_CHANNEL, STREAM_CHAIN_TIPS, STREAM_COLLABORATION, STREAM_CONNECT_TIMEOUT_MS, STREAM_DEDUPE_CAPACITY, STREAM_ELECTION_CHANNEL, STREAM_ENTITIES, STREAM_EVENTS_CHANNEL, STREAM_GROUPS, STREAM_HANDSHAKE_TIMEOUT_MS, STREAM_HEARTBEAT_INTERVAL_MS, STREAM_HEARTBEAT_TIMEOUT_MS, STREAM_HTTP_TIMEOUT_MS, STREAM_LABELS, STREAM_LEADER_HEARTBEAT_MS, STREAM_LEADER_STALE_MS, STREAM_MESSAGES, STREAM_NOTIFICATIONS, STREAM_PING_INTERVAL_MS, STREAM_PING_TIMEOUT_MS, STREAM_REGION_FAILOVER_THRESHOLD, STREAM_REGION_KEY, STREAM_STREAMED_TRANSACTIONS, STREAM_TAGS, STREAM_TRANSACTION_GROUPS, SUBSCRIBE_QUERY, StreamConnection, StreamingClient, StreamingContext, StreamingProvider, TEAM_CHANNEL_PREFIX, aliasEndpointResolver, appSyncRealtimeUrl, autheDedupeKey, computeBackoff, createAppSyncClientPool, createAppSyncPing, createAppSyncPublish, createAppSyncTransport, createAppSyncWebSocketClient, createFallbackTransport, createMockTransport, createPollTransport, createSseTransport, createStreamingClient, describeError, failoverRegion, hasBodyPointer, headForChain, initialConnectionState, isChannelScopedError, isKnownStream, isStreamBatch, parsePingResult, readResourceEvent, selectAutheStreamPayload, streamChannelId, streamChannelIdsForCaller, subscribeDescriptor, teamChannelPrincipal, unwrapStreamBatch, useAppSyncSubscription, useChainTips, useConnectionStatus, useOptionalStreamingClient, useStream, useStreamingClient };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map